// Project area: grouped side nav with a setup checklist (design flag 3).
import { Link, Outlet } from "@tanstack/react-router";
import { Component, type ReactNode } from "react";
import { ConvexError } from "convex/values";
import { Check, Circle, Lock } from "lucide-react";
import { ProjectProvider, useProject } from "@/hooks/project";
import { Badge, Empty, PageHeader, Spinner, cn, fmtDate } from "@/components/ui";
import { projectRoute } from "@/router";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useRouterState } from "@tanstack/react-router";

function ScreenBoundary({ children }: { children: ReactNode }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  return <ErrorBoundary resetKey={path}>{children}</ErrorBoundary>;
}
import type { Id } from "../../../convex/_generated/dataModel";

export function ProjectLayout() {
  const { projectId } = projectRoute.useParams();
  return (
    <NotFoundBoundary key={projectId}>
      <ProjectProvider projectId={projectId as Id<"projects">} fallback={<Spinner label="Loading project" />}>
        <Shell />
      </ProjectProvider>
    </NotFoundBoundary>
  );
}

/**
 * A link to a project in another organisation (or a deleted one) makes the bundle query throw "Project not found".
 * Say so instead of showing the crash screen; anything else goes on to the app's error boundary.
 */
class NotFoundBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state = { error: null as unknown };
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!(error instanceof ConvexError && error.data === "Project not found")) throw error;
    return (
      <div className="p-6">
        <PageHeader title="Project not found" />
        <Empty
          action={
            <Link to="/" className="font-medium text-brand-700 underline">
              Go to your projects
            </Link>
          }
        >
          This project isn't in the organisation you're signed in to. It may belong to another organisation (switch organisations at the top left if you're a member of it), or it has been deleted.
        </Empty>
      </div>
    );
  }
}

function Shell() {
  const { project, b, periods, currentIdx, issueCount } = useProject();
  const pid = project._id;
  const cur = periods[currentIdx];
  // Numbered setup order (Cesar's test, 34:03: "maybe a numbering order, steps one, two, three...").
  const checklist = [
    { label: "Breakdowns (DBS, WBS...)", done: b.nodes.some((n) => n.type === "dbs"), to: "breakdowns" },
    { label: "Calendar", done: b.periods.length > 0, to: "calendar" },
    { label: "Chains & milestones", done: b.chains.length > 0, to: "chains" },
    { label: "Deliverables + planned dates", done: b.deliverables.length > 0 && b.deliverables.every((d) => d.steps.some((s) => s.planned)), to: "deliverables" },
    { label: "Budget (matches deliverables)", done: b.budgetCells.length > 0, to: "budget" },
    { label: "Lock the baseline", done: project.baselineLocked, to: "periods" },
    { label: "Each period: earned dates", done: b.snapshots.length > 0, to: "progress" },
    { label: "Each period: actual hours", done: b.actuals.length > 0, to: "actuals" },
    { label: "Each period: close it", done: b.snapshots.length > 0, to: "periods" },
  ];
  const nextStep = checklist.findIndex((c) => !c.done);
  const Item = ({ to, children, exact }: { to: string; children: ReactNode; exact?: boolean }) => (
    <Link
      to={`/p/$projectId${to ? "/" + to : ""}` as "/p/$projectId"}
      params={{ projectId: pid }}
      activeOptions={{ exact: exact ?? false }}
      className="block rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-100"
      activeProps={{ className: "block rounded px-2 py-1 text-sm bg-brand-50 font-medium text-brand-700" }}
    >
      {children}
    </Link>
  );
  const Group = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="mt-3">
      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</div>
      {children}
    </div>
  );
  return (
    <div className="flex h-full print:block print:h-auto">
      <aside className="no-print w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-surface p-3">
        <div className="px-2">
          <div className="text-xs text-slate-500">{project.client}</div>
          <div className="font-semibold leading-tight">
            {project.code} <span className="font-normal text-slate-600">{project.name}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {cur && <Badge tone="blue">P{cur.number} · {fmtDate(cur.cutoff)}</Badge>}
            {project.baselineLocked && (
              <Badge tone="amber">
                <Lock className="mr-0.5 size-3" /> baseline
              </Badge>
            )}
          </div>
        </div>
        <Group title="Overview">
          <Item to="" exact>
            Dashboard
          </Item>
        </Group>
        <Group title="Setup">
          <Item to="setup">Parameters</Item>
          <Item to="breakdowns">Breakdowns</Item>
          <Item to="calendar">Calendar</Item>
          <Item to="chains">Chains & milestones</Item>
        </Group>
        <Group title="Register">
          <Item to="deliverables">Deliverables</Item>
          <Item to="progress">
            Progress update{" "}
            {issueCount.errors > 0 && (
              <Badge tone="red" className="ml-1">
                {issueCount.errors}
              </Badge>
            )}
          </Item>
        </Group>
        <Group title="Budget & cost">
          <Item to="budget">Budget & change orders</Item>
          <Item to="actuals">Actual hours</Item>
          <Item to="periods">Periods & baseline</Item>
        </Group>
        <Group title="Reports">
          <Item to="reports/summary">Progress summary</Item>
          {/* FIXME(before launch): two progress-curve layouts under test; keep one (see ExperimentNote in Reports.tsx). */}
          <Item to="reports/curve">
            Progress curve <Badge tone="amber" className="ml-1">A</Badge>
          </Item>
          <Item to="reports/curve-combined">
            Progress curve <Badge tone="amber" className="ml-1">B</Badge>
          </Item>
          <Item to="reports/histogram">Histogram</Item>
          <Item to="reports/productivity">Productivity</Item>
          <Item to="reports/worklist">Work items list</Item>
          <Item to="reports/milestones">Milestone report</Item>
        </Group>
        <Group title="History">
          <Item to="audit">Audit log</Item>
        </Group>
        <div className="mt-4 rounded-md border border-slate-200 p-2">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Steps</div>
          {checklist.map((c, i) => (
            <Link
              key={c.label}
              to={`/p/$projectId/${c.to}` as "/p/$projectId"}
              params={{ projectId: pid }}
              className={cn("flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-slate-600 hover:text-slate-900", i === nextStep && "bg-brand-50 font-medium text-brand-700")}
            >
              {c.done ? <Check className="size-3.5 shrink-0 text-emerald-600" /> : <Circle className="size-3.5 shrink-0 text-slate-300" />}
              <span className={cn(c.done && "text-slate-400")}>
                {i + 1}. {c.label}
              </span>
            </Link>
          ))}
        </div>
      </aside>
      <main className="min-w-0 flex-1 overflow-y-auto p-6 print:overflow-visible print:p-0">
        <ScreenBoundary>
          <Outlet />
        </ScreenBoundary>
      </main>
    </div>
  );
}
